// ReviewEntriesBeta.js — v2: stat card context, full comments, calendar views, admin create-on-behalf
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Clock, Search, X, ChevronRight,
  Check, CheckCircle, XCircle, Send, Edit3, Trash2, Copy, RefreshCw,
  Users, Calendar, Grid3x3, Layers,
  ArrowUpDown, ArrowUp, ArrowDown, Sparkles, List, Grid,
  DollarSign, User, Building2, FolderOpen, Inbox,
  SlidersHorizontal, FileText, MessageSquare,
  RotateCcw, AlertCircle, UserCheck, UserPlus, Plus
} from 'lucide-react';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';
import CalendarView from './time-entries/CalendarView';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     color: 'bg-slate-100 text-slate-600',  dot: 'bg-slate-400' },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500'  },
  in_review: { label: 'In Review', color: 'bg-amber-100 text-amber-700',  dot: 'bg-amber-500' },
  approved:  { label: 'Approved',  color: 'bg-green-100 text-green-700',  dot: 'bg-green-500' },
  rejected:  { label: 'Rejected',  color: 'bg-red-100 text-red-700',      dot: 'bg-red-500'   },
};

const GROUP_OPTIONS = [
  { value: 'none',    label: 'None',    icon: List },
  { value: 'user',    label: 'User',    icon: Users },
  { value: 'client',  label: 'Client',  icon: Building2 },
  { value: 'project', label: 'Project', icon: FolderOpen },
  { value: 'status',  label: 'Status',  icon: Layers },
  { value: 'date',    label: 'Date',    icon: Calendar },
];

const SORT_OPTIONS = [
  { value: 'entry_date',     label: 'Entry Date' },
  { value: 'submitted_at',   label: 'Submitted' },
  { value: 'duration_hours', label: 'Duration' },
  { value: 'task_name',      label: 'Task Name' },
  { value: 'created_at',     label: 'Created' },
];

const VIEW_MODES = [
  { id: 'list',  label: 'List',  icon: List },
  { id: 'day',   label: 'Day',   icon: Calendar },
  { id: 'week',  label: 'Week',  icon: Grid3x3 },
  { id: 'month', label: 'Month', icon: Grid },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const fmtHours = (h = 0) => {
  const hours = Math.floor(h); const mins = Math.round((h - hours) * 60);
  return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
};
const fmtDate = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const fmtCurrency = (n = 0) => `$${(n || 0).toFixed(2)}`;
const todayStr = () => new Date().toISOString().split('T')[0];

// ─── STAT CARD ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, total, sub, totalHoursSub, icon: Icon, color, onClick, active }) => {
  const isFiltered = total !== undefined && String(value) !== String(total);
  return (
    <button onClick={onClick}
      className={`flex-1 min-w-0 bg-white rounded-xl border-2 p-4 text-left transition-all hover:shadow-md ${
        active ? 'border-blue-500 shadow-md shadow-blue-100' : 'border-slate-200 hover:border-slate-300'
      }`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        {active && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1 flex-shrink-0" />}
      </div>
      <div className="flex items-baseline gap-1.5 mb-0.5 flex-wrap">
        <span className="text-2xl font-bold text-slate-800 leading-none">{value}</span>
        {isFiltered && <span className="text-sm text-slate-400 font-medium leading-none">of {total}</span>}
      </div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      {sub && <div className={`text-xs mt-0.5 ${isFiltered ? 'text-blue-500 font-medium' : 'text-slate-400'}`}>{sub}{isFiltered && totalHoursSub ? ` · ${totalHoursSub} total` : ''}</div>}
      {isFiltered && <div className="mt-1.5 flex items-center gap-1 text-xs text-blue-600 font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" />filtered</div>}
    </button>
  );
};

// ─── SMALL HELPERS ────────────────────────────────────────────────────────────

const Checkbox = ({ checked, indeterminate, onChange }) => {
  const ref = useRef();
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange}
    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />;
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
  );
};

// ─── COMMENTS SECTION ────────────────────────────────────────────────────────

const CommentsSection = ({ entryId, initialComments = [], currentUserId, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadComments = async () => {
    const { data, error: fetchErr } = await supabase
      .from('time_entry_comments')
      .select(`id, comment_text, created_at, is_system_comment, user_profiles!time_entry_comments_user_id_fkey(full_name, email)`)
      .eq('time_entry_id', entryId).order('created_at', { ascending: true });
    if (!fetchErr && data) setComments(data);
    setLoading(false);
  };

  // Always fetch fresh from DB — the entry object's comments array is often stale
  useEffect(() => { loadComments(); }, [entryId]);

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true); setError('');
    try {
      const { error: err } = await supabase.from('time_entry_comments').insert({
        time_entry_id: entryId, user_id: currentUserId, comment_text: newComment.trim(),
      });
      if (err) throw err;
      setNewComment('');
      await loadComments();
      if (onCommentAdded) onCommentAdded();
    } catch (e) {
      setError('Failed to save. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <MessageSquare className="w-3.5 h-3.5" />Comments {comments.length > 0 && `(${comments.length})`}
      </div>
      <div className="space-y-2 mb-3">
        {loading
          ? <p className="text-xs text-slate-400 italic py-2">Loading comments...</p>
          : comments.length === 0
          ? <p className="text-xs text-slate-400 italic py-2">No comments yet.</p>
          : comments.map((c, i) => (
            <div key={c.id || i} className={`rounded-xl p-3 text-sm ${c.is_system_comment ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-700 text-xs">{c.is_system_comment ? '🤖 System' : (c.user_profiles?.full_name || c.user_profiles?.email || 'Unknown')}</span>
                <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
              </div>
              <p className="text-slate-600 leading-relaxed">{c.comment_text}</p>
            </div>
          ))
        }
      </div>
      <div className="space-y-2">
        <textarea rows={2} placeholder="Add a comment... (Ctrl+Enter to submit)"
          value={newComment} onChange={e => setNewComment(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitComment(); }}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-slate-50 placeholder-slate-400" />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button onClick={submitComment} disabled={submitting || !newComment.trim()}
          className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-colors">
          <Send className="w-3.5 h-3.5" />{submitting ? 'Posting...' : 'Post Comment'}
        </button>
      </div>
    </div>
  );
};

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────

const EditModal = ({ entry, teamMembers, clients, projects, userRole, onSave, onClose }) => {
  const [values, setValues] = useState({
    task_name: entry.task_name || '', description: entry.description || '',
    duration_hours: entry.duration_hours || 0, entry_date: entry.entry_date || todayStr(),
    is_billable: entry.is_billable ?? true, client_id: entry.client_id || '',
    project_id: entry.project_id || '', user_id: entry.user_id || '', priority: entry.priority || 'medium',
  });
  const set = (k, v) => setValues(p => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2"><Edit3 className="w-5 h-5 text-blue-600" /><h2 className="text-lg font-semibold text-slate-800">Edit Entry</h2></div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Task Name *</label>
            <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={values.task_name} onChange={e => set('task_name', e.target.value)} placeholder="What was worked on?" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Duration (hrs)</label>
              <input type="number" min="0" step="0.25"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.duration_hours} onChange={e => set('duration_hours', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Date</label>
              <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.entry_date} onChange={e => set('entry_date', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Client</label>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">No Client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Project</label>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.project_id} onChange={e => set('project_id', e.target.value)}>
                <option value="">No Project</option>
                {projects.filter(p => !values.client_id || p.client_id === values.client_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {userRole === 'admin' && teamMembers?.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-blue-500" />Reassign To (Admin)</span>
              </label>
              <select className="w-full px-3 py-2 border border-blue-200 bg-blue-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.user_id} onChange={e => set('user_id', e.target.value)}>
                <option value="">Keep Current User</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name || m.email} ({m.role})</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Description</label>
            <textarea rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              value={values.description} onChange={e => set('description', e.target.value)} placeholder="Additional notes..." />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={values.is_billable} onChange={e => set('is_billable', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-slate-700 font-medium">Billable</span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 font-medium">Priority:</span>
              {['low','medium','high'].map(p => (
                <button key={p} onClick={() => set('priority', p)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    values.priority === p ? p==='high'?'bg-red-100 text-red-700':p==='medium'?'bg-amber-100 text-amber-700':'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave(entry.id, values)} disabled={!values.task_name.trim()}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">Save Changes</button>
        </div>
      </div>
    </div>
  );
};

// ─── CREATE ON BEHALF MODAL ───────────────────────────────────────────────────

const CreateOnBehalfModal = ({ teamMembers, clients, projects, onSave, onClose }) => {
  const [values, setValues] = useState({
    user_id: '', task_name: '', description: '', duration_hours: 1,
    entry_date: todayStr(), is_billable: true, client_id: '', project_id: '',
    priority: 'medium', status: 'draft',
  });
  const set = (k, v) => setValues(p => ({ ...p, [k]: v }));
  const valid = values.user_id && values.task_name.trim() && values.duration_hours > 0;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-violet-100 rounded-xl flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-violet-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Log Entry on Behalf</h2>
              <p className="text-xs text-slate-400">Admin · Creates entry for another team member</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <label className="block text-xs font-bold text-violet-700 uppercase tracking-wide mb-2">
              <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Team Member *</span>
            </label>
            <select className="w-full px-3 py-2.5 border border-violet-200 bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 font-medium"
              value={values.user_id} onChange={e => set('user_id', e.target.value)}>
              <option value="">Select team member...</option>
              {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name || m.email} ({m.role})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Task Name *</label>
            <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={values.task_name} onChange={e => set('task_name', e.target.value)} placeholder="What was worked on?" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Duration (hrs) *</label>
              <input type="number" min="0.25" step="0.25"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.duration_hours} onChange={e => set('duration_hours', parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Date *</label>
              <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.entry_date} onChange={e => set('entry_date', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Client</label>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">No Client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Project</label>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.project_id} onChange={e => set('project_id', e.target.value)}>
                <option value="">No Project</option>
                {projects.filter(p => !values.client_id || p.client_id === values.client_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Description</label>
            <textarea rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              value={values.description} onChange={e => set('description', e.target.value)} placeholder="Optional notes..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Initial Status</label>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={values.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={values.is_billable} onChange={e => set('is_billable', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700 font-medium">Billable</span>
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave(values)} disabled={!valid}
            className="px-5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2">
            <UserPlus className="w-4 h-4" />Create Entry
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── WORKFLOW MODAL ───────────────────────────────────────────────────────────

const WorkflowModal = ({ entry, teamMembers, userRole, onAction, onClose }) => {
  const [comment, setComment] = useState('');
  const [assignee, setAssignee] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const isManager = ['admin','manager'].includes(userRole);
  const getActions = () => {
    switch (entry.status) {
      case 'draft':     return [{ id:'submitted', label:'Submit for Review', icon:Send, color:'blue' }];
      case 'submitted':
      case 'in_review': {
        const a = [];
        if (isManager) {
          a.push({ id:'approved', label:'Approve', icon:CheckCircle, color:'green' });
          a.push({ id:'rejected', label:'Reject', icon:XCircle, color:'red' });
          if (entry.status==='in_review') a.push({ id:'submitted', label:'Send Back', icon:RotateCcw, color:'amber' });
        }
        return a;
      }
      case 'rejected':  return [{ id:'draft', label:'Move to Draft', icon:Edit3, color:'slate' }];
      case 'approved':  return isManager ? [{ id:'submitted', label:'Reopen for Review', icon:RotateCcw, color:'amber' }] : [];
      default: return [];
    }
  };
  const actions = getActions();
  const colorMap = { green:'bg-green-50 border-green-200 text-green-700 hover:bg-green-100', red:'bg-red-50 border-red-200 text-red-700 hover:bg-red-100', blue:'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100', amber:'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100', slate:'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100' };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div><h2 className="text-lg font-semibold text-slate-800">Update Status</h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate max-w-xs">{entry.task_name}</p></div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {actions.length === 0
            ? <div className="text-center py-6"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" /><p className="font-medium text-slate-500">No actions available</p></div>
            : <>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Select Action</label>
                <div className="space-y-2">
                  {actions.map(a => (
                    <button key={a.id} onClick={() => setSelectedAction(a.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${selectedAction===a.id ? colorMap[a.color] : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}>
                      <a.icon className="w-4 h-4 flex-shrink-0" />{a.label}
                      {selectedAction===a.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Comment {selectedAction==='rejected' ? '(Required)' : '(Optional)'}
                </label>
                <textarea rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a note..." />
              </div>
              {isManager && teamMembers?.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Assign Reviewer (Optional)</label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={assignee} onChange={e => setAssignee(e.target.value)}>
                    <option value="">No specific reviewer</option>
                    {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.full_name || m.email} ({m.role})</option>)}
                  </select>
                </div>
              )}
            </>
          }
        </div>
        {actions.length > 0 && (
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button onClick={() => { onAction(entry.id, selectedAction, comment, assignee||null); onClose(); }}
              disabled={!selectedAction||(selectedAction==='rejected'&&!comment.trim())}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">Confirm</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────

const DetailPanel = ({ entry, teamMembers, clients, projects, userRole, currentUserId, onClose, onEdit, onStatusUpdate, onCommentAdded }) => {
  const client = clients?.find(c => c.id === entry.client_id);
  const project = projects?.find(p => p.id === entry.project_id);
  const assignedUser = teamMembers?.find(m => m.user_id === entry.user_id);
  const revenue = entry.is_billable ? (entry.duration_hours * (entry.hourly_rate || 0)) : 0;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap"><StatusPill status={entry.status} />
              {entry.is_billable && <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Billable</span>}
            </div>
            <h2 className="text-lg font-bold text-slate-800 truncate">{entry.task_name}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{fmtDate(entry.entry_date)}</p>
          </div>
          <button onClick={onClose} className="ml-3 p-2 hover:bg-slate-100 rounded-lg flex-shrink-0"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3.5"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Duration</div><div className="text-2xl font-bold text-slate-800">{fmtHours(entry.duration_hours)}</div></div>
            <div className="bg-slate-50 rounded-xl p-3.5"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Revenue</div><div className="text-2xl font-bold text-emerald-700">{fmtCurrency(revenue)}</div></div>
          </div>
          <div className="space-y-3">
            {[
              { label:'User',    value: assignedUser?.full_name || assignedUser?.email || 'Unknown', icon:User },
              { label:'Client',  value: client?.name || entry.client_name || 'No Client', icon:Building2 },
              { label:'Project', value: project?.name || entry.project_name || 'No Project', icon:FolderOpen },
              { label:'Rate',    value: entry.hourly_rate ? `$${entry.hourly_rate}/hr` : 'No rate', icon:DollarSign },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-slate-500" /></div>
                <div><div className="text-xs text-slate-400 font-medium">{label}</div><div className="text-sm text-slate-700 font-semibold">{value}</div></div>
              </div>
            ))}
          </div>
          {entry.description && (
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Description</div>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3.5 leading-relaxed">{entry.description}</p>
            </div>
          )}
          {entry.tags?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">{entry.tags.map(t => <span key={t} className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-medium">{t}</span>)}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Timeline</div>
            <div className="space-y-1.5 text-xs text-slate-500">
              {entry.created_at   && <div className="flex justify-between"><span>Created</span><span className="font-medium">{fmtDate(entry.created_at)}</span></div>}
              {entry.submitted_at && <div className="flex justify-between"><span>Submitted</span><span className="font-medium">{fmtDate(entry.submitted_at)}</span></div>}
              {entry.approved_at  && <div className="flex justify-between text-green-600"><span>Approved</span><span className="font-medium">{fmtDate(entry.approved_at)}</span></div>}
              {entry.rejected_at  && <div className="flex justify-between text-red-600"><span>Rejected</span><span className="font-medium">{fmtDate(entry.rejected_at)}</span></div>}
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <CommentsSection entryId={entry.id} initialComments={entry.comments || []} currentUserId={currentUserId} onCommentAdded={onCommentAdded} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
          <button onClick={() => { onClose(); onEdit(entry); }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <Edit3 className="w-4 h-4" />Edit
          </button>
          <button onClick={() => { onClose(); onStatusUpdate(entry); }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
            <RefreshCw className="w-4 h-4" />Workflow
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ENTRY ROW ────────────────────────────────────────────────────────────────

const EntryRow = ({ entry, selected, onToggle, onEdit, onDelete, onDuplicate, onStatusUpdate, onView, userRole, teamMembers, clients, projects }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef();
  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  const isManager = ['admin','manager'].includes(userRole);
  const revenue = entry.is_billable ? (entry.duration_hours * (entry.hourly_rate || 0)) : 0;
  const commentCount = entry.comments?.length || 0;
  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors group ${selected ? 'bg-blue-50/40' : ''}`}>
      <td className="pl-4 pr-2 py-3"><Checkbox checked={selected} onChange={() => onToggle(entry.id)} /></td>
      <td className="px-3 py-3">
        <div className="max-w-xs">
          <div className="font-semibold text-slate-800 text-sm truncate">{entry.task_name}</div>
          {entry.description && <div className="text-xs text-slate-400 truncate mt-0.5">{entry.description}</div>}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {entry.is_split && <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">Split</span>}
            {commentCount > 0 && (
              <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <MessageSquare className="w-2.5 h-2.5" />{commentCount}
              </span>
            )}
            {entry.tags?.slice(0,2).map(t => <span key={t} className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{t}</span>)}
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="text-sm text-slate-600">{entry.user_name || teamMembers?.find(m => m.user_id===entry.user_id)?.full_name || '—'}</div>
        <div className="text-xs text-slate-400">{entry.bill_rate_role || ''}</div>
      </td>
      <td className="px-3 py-3">
        <div className="text-sm font-medium text-slate-700">{entry.client_name || clients?.find(c => c.id===entry.client_id)?.name || '—'}</div>
        <div className="text-xs text-slate-400 truncate max-w-24">{entry.project_name || projects?.find(p => p.id===entry.project_id)?.name || ''}</div>
      </td>
      <td className="px-3 py-3 text-sm font-semibold text-slate-700">{fmtDate(entry.entry_date)}</td>
      <td className="px-3 py-3">
        <div className="text-sm font-bold text-slate-800">{fmtHours(entry.duration_hours)}</div>
        {entry.is_billable && revenue > 0 && <div className="text-xs text-emerald-600 font-medium">{fmtCurrency(revenue)}</div>}
      </td>
      <td className="px-3 py-3"><StatusPill status={entry.status} /></td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isManager && (entry.status==='submitted'||entry.status==='in_review') && (
            <button onClick={() => onStatusUpdate(entry,'approved')} title="Quick Approve"
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onView(entry)} title="View Details & Comments" className="relative p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {commentCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-500 rounded-full" />}
          </button>
          <div ref={menuRef} className="relative">
            <button onClick={() => setShowMenu(s => !s)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 bg-white border border-slate-200 rounded-xl shadow-xl z-30 w-44 py-1.5">
                <button onClick={() => { onEdit(entry); setShowMenu(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50"><Edit3 className="w-3.5 h-3.5 text-slate-400" />Edit Entry</button>
                <button onClick={() => { onStatusUpdate(entry); setShowMenu(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50"><RefreshCw className="w-3.5 h-3.5 text-slate-400" />Change Status</button>
                <button onClick={() => { onDuplicate(entry); setShowMenu(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50"><Copy className="w-3.5 h-3.5 text-slate-400" />Duplicate</button>
                <div className="border-t border-slate-100 my-1" />
                <button onClick={() => { if (window.confirm('Delete this entry?')) { onDelete(entry.id); } setShowMenu(false); }} className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" />Delete</button>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// ─── GROUP HEADER ─────────────────────────────────────────────────────────────

const GroupHeader = ({ label, count, hours, revenue, expanded, onToggle, onSelectAll, allSelected, anySelected }) => (
  <tr className="bg-slate-100/80 border-b border-slate-200">
    <td className="pl-4 pr-2 py-2.5"><Checkbox checked={allSelected} indeterminate={anySelected&&!allSelected} onChange={onSelectAll} /></td>
    <td colSpan={6} className="px-3 py-2.5">
      <button onClick={onToggle} className="flex items-center gap-2.5 text-left w-full">
        <span className={`transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}><ChevronRight className="w-4 h-4 text-slate-500" /></span>
        <span className="font-semibold text-slate-700 text-sm">{label}</span>
        <span className="text-xs text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-200">{count} {count===1?'entry':'entries'}</span>
        <span className="text-xs font-semibold text-slate-600">{fmtHours(hours)}</span>
        {revenue > 0 && <span className="text-xs font-semibold text-emerald-600">{fmtCurrency(revenue)}</span>}
      </button>
    </td>
    <td className="px-3 py-2.5" />
  </tr>
);

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

const ReviewEntriesBeta = () => {
  const { user } = useAuth();
  const { currentTenant, userRole } = useTenant();
  const isManager = ['admin','manager'].includes(userRole);

  const { timeEntries: rawEntries, loading: entriesLoading, createTimeEntry, updateTimeEntry, deleteTimeEntry, refetch } =
    useTimeEntries({ sortBy: 'entry_date', sortOrder: 'desc' });

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [refLoading, setRefLoading] = useState(true);

  const [viewMode, setViewMode] = useState('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState({ search:'', status:'all', clientId:'all', projectId:'all', userId:'all', billable:'all', priority:'all', startDate:'', endDate:'' });
  const [groupBy, setGroupBy] = useState('none');
  const [sortBy, setSortBy] = useState('entry_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const [editingEntry, setEditingEntry] = useState(null);
  const [workflowEntry, setWorkflowEntry] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [showCreateOnBehalf, setShowCreateOnBehalf] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); }, []);

  useEffect(() => {
    if (!currentTenant) return;
    const load = async () => {
      setRefLoading(true);
      try {
        const [{ data: c }, { data: p }, { data: t }] = await Promise.all([
          supabase.from('clients').select('*').eq('tenant_id', currentTenant.id).order('name'),
          supabase.from('projects').select('*').eq('tenant_id', currentTenant.id).order('name'),
          supabase.from('tenant_memberships').select('user_id, role, user_profiles!tenant_memberships_user_id_fkey(full_name, email)').eq('tenant_id', currentTenant.id).eq('status','active'),
        ]);
        setClients(c || []);
        setProjects(p || []);
        setTeamMembers((t||[]).map(m => ({ user_id:m.user_id, role:m.role, full_name:m.user_profiles?.full_name, email:m.user_profiles?.email })));
      } catch (e) { console.error(e); } finally { setRefLoading(false); }
    };
    load();
  }, [currentTenant]);

  // Stats always from full rawEntries (ignore status/billable filter for "total" context)
  const allStats = useMemo(() => {
    const all = Array.isArray(rawEntries) ? rawEntries : [];
    return {
      total: all.length,
      totalHours: all.reduce((s,e) => s+(e.duration_hours||0), 0),
      pending: all.filter(e => e.status==='submitted'||e.status==='in_review').length,
      approved: all.filter(e => e.status==='approved').length,
      drafts: all.filter(e => e.status==='draft').length,
      revenue: all.filter(e => e.is_billable).reduce((s,e) => s+(e.duration_hours*(e.hourly_rate||0)), 0),
    };
  }, [rawEntries]);

  const filteredEntries = useMemo(() => {
    let entries = Array.isArray(rawEntries) ? [...rawEntries] : [];
    if (filters.status !== 'all') entries = filters.status==='pending' ? entries.filter(e=>e.status==='submitted'||e.status==='in_review') : entries.filter(e=>e.status===filters.status);
    if (filters.clientId !== 'all') entries = entries.filter(e => e.client_id===filters.clientId);
    if (filters.projectId !== 'all') entries = entries.filter(e => e.project_id===filters.projectId);
    if (filters.userId !== 'all') entries = entries.filter(e => e.user_id===filters.userId);
    if (filters.billable === 'billable') entries = entries.filter(e => e.is_billable===true);
    if (filters.billable === 'nonbillable') entries = entries.filter(e => e.is_billable===false);
    if (filters.priority !== 'all') entries = entries.filter(e => e.priority===filters.priority);
    if (filters.startDate) entries = entries.filter(e => e.entry_date >= filters.startDate);
    if (filters.endDate) entries = entries.filter(e => e.entry_date <= filters.endDate);
    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      entries = entries.filter(e => e.task_name?.toLowerCase().includes(q)||e.description?.toLowerCase().includes(q)||e.client_name?.toLowerCase().includes(q)||e.project_name?.toLowerCase().includes(q)||e.user_name?.toLowerCase().includes(q)||e.tags?.some(t=>t.toLowerCase().includes(q)));
    }
    entries.sort((a,b) => {
      let av = sortBy==='duration_hours'?parseFloat(a[sortBy])||0:a[sortBy]||'';
      let bv = sortBy==='duration_hours'?parseFloat(b[sortBy])||0:b[sortBy]||'';
      const cmp = av<bv?-1:av>bv?1:0; return sortDir==='asc'?cmp:-cmp;
    });
    return entries;
  }, [rawEntries, filters, sortBy, sortDir]);

  const filteredStats = useMemo(() => {
    const all = filteredEntries;
    return {
      total: all.length,
      totalHours: all.reduce((s,e) => s+(e.duration_hours||0), 0),
      pending: all.filter(e => e.status==='submitted'||e.status==='in_review').length,
      approved: all.filter(e => e.status==='approved').length,
      drafts: all.filter(e => e.status==='draft').length,
      revenue: all.filter(e => e.is_billable).reduce((s,e) => s+(e.duration_hours*(e.hourly_rate||0)), 0),
    };
  }, [filteredEntries]);

  const groupedEntries = useMemo(() => {
    if (groupBy==='none') return [{ key:'__all__', label:'All', entries:filteredEntries }];
    const map = {};
    filteredEntries.forEach(e => {
      let key, label;
      switch(groupBy) {
        case 'user':    key=e.user_id||'unknown'; label=e.user_name||teamMembers.find(m=>m.user_id===e.user_id)?.full_name||'Unknown User'; break;
        case 'client':  key=e.client_id||'no_client'; label=e.client_name||clients.find(c=>c.id===e.client_id)?.name||'No Client'; break;
        case 'project': key=e.project_id||'no_project'; label=e.project_name||projects.find(p=>p.id===e.project_id)?.name||'No Project'; break;
        case 'status':  key=e.status||'unknown'; label=STATUS_CONFIG[e.status]?.label||e.status||'Unknown'; break;
        case 'date':    key=e.entry_date||'unknown'; label=fmtDate(e.entry_date); break;
        default: key='all'; label='All';
      }
      if (!map[key]) map[key]={ key, label, entries:[] };
      map[key].entries.push(e);
    });
    return Object.values(map).sort((a,b) => b.entries.length-a.entries.length);
  }, [filteredEntries, groupBy, teamMembers, clients, projects]);

  useEffect(() => {
    if (groupBy!=='none') { const exp={}; groupedEntries.forEach(g => { exp[g.key]=true; }); setExpandedGroups(exp); }
  }, [groupBy]);

  const visibleIds = filteredEntries.map(e => e.id);
  const allSelected = visibleIds.length>0 && visibleIds.every(id => selected.includes(id));
  const anySelected = selected.some(id => visibleIds.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);
  const toggleOne = (id) => setSelected(p => p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleGroup = (groupEntries) => { const ids=groupEntries.map(e=>e.id); const allGs=ids.every(id=>selected.includes(id)); setSelected(p=>allGs?p.filter(id=>!ids.includes(id)):[...new Set([...p,...ids])]); };
  const toggleSort = (field) => { if(sortBy===field) setSortDir(d=>d==='asc'?'desc':'asc'); else { setSortBy(field); setSortDir('desc'); } };
  const SortIcon = ({ field }) => { if(sortBy!==field) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-300" />; return sortDir==='asc'?<ArrowUp className="w-3.5 h-3.5 text-blue-500" />:<ArrowDown className="w-3.5 h-3.5 text-blue-500" />; };
  const activeFilterCount = [filters.status!=='all',filters.clientId!=='all',filters.projectId!=='all',filters.userId!=='all',filters.billable!=='all',filters.priority!=='all',filters.startDate,filters.endDate].filter(Boolean).length;
  const isFiltered = filteredEntries.length !== (rawEntries?.length || 0);

  const handleStatusUpdate = async (entryOrId, newStatus, comment, assignedTo) => {
    const id = typeof entryOrId==='string'?entryOrId:entryOrId?.id;
    const status = typeof newStatus==='string'?newStatus:null;
    if (!status) return;
    const updates = { status };
    if (status==='submitted') updates.submitted_at=new Date().toISOString();
    if (status==='approved') { updates.approved_at=new Date().toISOString(); updates.approved_by=user.id; }
    if (status==='rejected') { updates.rejected_at=new Date().toISOString(); updates.rejected_by=user.id; if(comment) updates.rejection_reason=comment; }
    if (assignedTo) updates.assigned_to=assignedTo;
    const { error } = await updateTimeEntry(id, updates);
    if (error) { showToast('Error: '+error.message,'error'); return; }
    if (comment) await supabase.from('time_entry_comments').insert({ time_entry_id:id, user_id:user.id, comment_text:comment });
    showToast(`Entry ${status.replace('_',' ')} successfully`);
  };

  const handleEdit = async (entryId, values) => {
    const id = typeof entryId==='string'?entryId:entryId?.id;
    const original = rawEntries.find(e=>e.id===id);
    const updates = { ...values };
    if (values.user_id && values.user_id!==original?.user_id && userRole==='admin') {
      updates.reassigned_at=new Date().toISOString(); updates.reassigned_by=user.id; updates.previous_user_id=original.user_id;
      const from=teamMembers.find(m=>m.user_id===original.user_id); const to=teamMembers.find(m=>m.user_id===values.user_id);
      await supabase.from('time_entry_comments').insert({ time_entry_id:id, user_id:user.id, comment_text:`Reassigned from ${from?.full_name||from?.email||'Unknown'} to ${to?.full_name||to?.email||'Unknown'} by admin.`, is_system_comment:true });
    }
    const { error } = await updateTimeEntry(id, updates);
    if (error) { showToast('Error: '+error.message,'error'); return; }
    setEditingEntry(null); showToast('Entry updated');
  };

  const handleDelete = async (entryId) => {
    const { error } = await deleteTimeEntry(entryId);
    if (error) { showToast('Error: '+error.message,'error'); return; }
    setSelected(p=>p.filter(id=>id!==entryId)); showToast('Entry deleted');
  };

  const handleDuplicate = async (entry) => {
    const { error } = await createTimeEntry({ client_id:entry.client_id, project_id:entry.project_id, task_name:entry.task_name, description:entry.description, duration_hours:entry.duration_hours, is_billable:entry.is_billable, entry_date:todayStr(), status:'draft' });
    if (error) { showToast('Error: '+error.message,'error'); return; }
    showToast('Duplicated as draft');
  };

  const handleCreateOnBehalf = async (values) => {
    try {
      const { error } = await supabase.from('time_entries').insert({
        ...values, tenant_id:currentTenant.id,
        ...(values.status==='submitted'?{submitted_at:new Date().toISOString()}:{}),
        ...(values.status==='approved'?{approved_at:new Date().toISOString(),approved_by:user.id}:{}),
      });
      if (error) throw error;
      setShowCreateOnBehalf(false); await refetch();
      const member = teamMembers.find(m=>m.user_id===values.user_id);
      showToast(`Entry created for ${member?.full_name||member?.email||'user'}`);
    } catch (e) { showToast('Error: '+e.message,'error'); }
  };

  const handleBulkAction = async (action) => {
    const ids = selected.filter(id=>visibleIds.includes(id));
    if (!ids.length) return;
    if (!window.confirm(`${action.charAt(0).toUpperCase()+action.slice(1)} ${ids.length} entries?`)) return;
    const updates = {};
    if (action==='approve') { updates.status='approved'; updates.approved_at=new Date().toISOString(); updates.approved_by=user.id; }
    if (action==='reject')  { updates.status='rejected'; updates.rejected_at=new Date().toISOString(); updates.rejected_by=user.id; }
    if (action==='submit')  { updates.status='submitted'; updates.submitted_at=new Date().toISOString(); }
    if (action==='delete') { for (const id of ids) await deleteTimeEntry(id); }
    else { for (const id of ids) await updateTimeEntry(id, updates); }
    setSelected([]); showToast(`${ids.length} entries ${action}d`);
  };

  const clearFilters = () => setFilters({ search:'', status:'all', clientId:'all', projectId:'all', userId:'all', billable:'all', priority:'all', startDate:'', endDate:'' });

  if (entriesLoading || refLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Loading entries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-sm font-semibold ${toast.type==='error'?'bg-red-600':'bg-slate-900'} text-white`}>
          {toast.type==='error'?<AlertCircle className="w-4 h-4" />:<CheckCircle className="w-4 h-4 text-green-400" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-slate-800">Review Entries</h1>
            <span className="text-xs font-bold bg-gradient-to-r from-violet-600 to-blue-600 text-white px-2.5 py-1 rounded-full">BETA</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            {isFiltered
              ? <><span className="font-semibold text-slate-700">{filteredEntries.length}</span> of {rawEntries.length} entries · {fmtHours(filteredStats.totalHours)}</>
              : <>{rawEntries.length} entries · {fmtHours(allStats.totalHours)}</>
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          {userRole==='admin' && (
            <button onClick={() => setShowCreateOnBehalf(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-colors">
              <UserPlus className="w-4 h-4" />Log for User
            </button>
          )}
          <button onClick={refetch} className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        <StatCard label="Total Entries" icon={Clock} color="bg-blue-50 text-blue-600"
          value={filteredStats.total} total={allStats.total}
          sub={fmtHours(filteredStats.totalHours)} totalHoursSub={fmtHours(allStats.totalHours)}
          onClick={() => setFilters(f=>({...f,status:'all',billable:'all'}))} active={filters.status==='all'&&filters.billable==='all'} />
        <StatCard label="Pending Review" icon={Inbox} color="bg-amber-50 text-amber-600"
          value={filteredStats.pending} total={allStats.pending}
          onClick={() => setFilters(f=>({...f,status:'pending'}))} active={filters.status==='pending'} />
        <StatCard label="Approved" icon={CheckCircle} color="bg-green-50 text-green-600"
          value={filteredStats.approved} total={allStats.approved}
          onClick={() => setFilters(f=>({...f,status:'approved'}))} active={filters.status==='approved'} />
        <StatCard label="Drafts" icon={FileText} color="bg-slate-100 text-slate-600"
          value={filteredStats.drafts} total={allStats.drafts}
          onClick={() => setFilters(f=>({...f,status:'draft'}))} active={filters.status==='draft'} />
        <StatCard label="Revenue" icon={DollarSign} color="bg-emerald-50 text-emerald-600"
          value={fmtCurrency(filteredStats.revenue)} total={fmtCurrency(allStats.revenue)}
          onClick={() => setFilters(f=>({...f,billable:'billable'}))} active={filters.billable==='billable'} />
      </div>

      {/* Toolbar + Table/Calendar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
          <div className="relative min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search entries..." value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))}
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" />
            {filters.search && <button onClick={()=>setFilters(f=>({...f,search:''}))} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" /></button>}
          </div>
          <button onClick={()=>setShowFilters(f=>!f)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${showFilters||activeFilterCount>0?'border-blue-300 bg-blue-50 text-blue-700':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <SlidersHorizontal className="w-4 h-4" />Filters
            {activeFilterCount>0 && <span className="bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
          </button>
          <div className="h-5 w-px bg-slate-200" />
          {/* View mode switcher */}
          <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
            {VIEW_MODES.map(v => (
              <button key={v.id} onClick={()=>setViewMode(v.id)} title={v.label}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode===v.id?'bg-white text-blue-600 shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                <v.icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>
          {viewMode==='list' && (
            <>
              <div className="h-5 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Group</span>
                <div className="flex items-center gap-0.5">
                  {GROUP_OPTIONS.map(g => (
                    <button key={g.value} onClick={()=>setGroupBy(g.value)} title={g.label}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 ${groupBy===g.value?'bg-blue-600 text-white':'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}>
                      <g.icon className="w-3.5 h-3.5" /><span className="hidden md:inline">{g.value==='none'?'None':g.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="px-4 py-4 border-b border-slate-100 bg-slate-50/50">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <select value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="all">All Statuses</option><option value="pending">Pending Review</option><option value="draft">Draft</option>
                <option value="submitted">Submitted</option><option value="in_review">In Review</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
              </select>
              <select value={filters.clientId} onChange={e=>setFilters(f=>({...f,clientId:e.target.value,projectId:'all'}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="all">All Clients</option>{clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={filters.projectId} onChange={e=>setFilters(f=>({...f,projectId:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="all">All Projects</option>{projects.filter(p=>filters.clientId==='all'||p.client_id===filters.clientId).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {isManager && (
                <select value={filters.userId} onChange={e=>setFilters(f=>({...f,userId:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="all">All Users</option>{teamMembers.map(m=><option key={m.user_id} value={m.user_id}>{m.full_name||m.email}</option>)}
                </select>
              )}
              <select value={filters.billable} onChange={e=>setFilters(f=>({...f,billable:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="all">All Types</option><option value="billable">Billable Only</option><option value="nonbillable">Non-billable</option>
              </select>
              <input type="date" value={filters.startDate} onChange={e=>setFilters(f=>({...f,startDate:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-600" />
              <input type="date" value={filters.endDate} onChange={e=>setFilters(f=>({...f,endDate:e.target.value}))} className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-600" />
            </div>
            {activeFilterCount>0 && <button onClick={clearFilters} className="mt-3 flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-semibold"><X className="w-3.5 h-3.5" />Clear all filters</button>}
          </div>
        )}

        {/* Bulk actions bar */}
        {selected.filter(id=>visibleIds.includes(id)).length>0 && (
          <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border-b border-blue-100 flex-wrap">
            <span className="text-sm font-semibold text-blue-800">{selected.filter(id=>visibleIds.includes(id)).length} selected</span>
            <div className="flex items-center gap-2">
              {isManager && <>
                <button onClick={()=>handleBulkAction('approve')} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700"><CheckCircle className="w-3.5 h-3.5" />Approve</button>
                <button onClick={()=>handleBulkAction('reject')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700"><XCircle className="w-3.5 h-3.5" />Reject</button>
              </>}
              <button onClick={()=>handleBulkAction('submit')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"><Send className="w-3.5 h-3.5" />Submit</button>
              <button onClick={()=>handleBulkAction('delete')} className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-600 border border-red-200 text-xs font-semibold rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" />Delete</button>
            </div>
            <button onClick={()=>setSelected([])} className="ml-auto text-xs text-blue-600 font-semibold">Clear</button>
          </div>
        )}

        {/* Calendar views */}
        {viewMode!=='list' && (
          <div className="p-4">
            <CalendarView
              key={`${viewMode}-${currentDate.toDateString()}`}
              timeEntries={filteredEntries} clients={clients} projects={projects}
              currentDate={currentDate} viewMode={viewMode} groupBy={groupBy}
              onDateChange={setCurrentDate}
              onEntryEdit={(entry) => setEditingEntry(entry)}
              onEntryDuplicate={handleDuplicate}
              onEntryView={(entry) => setDetailEntry(entry)}
              onEntryCreate={async (data) => { const { error } = await createTimeEntry(data); if (error) showToast('Error: '+error.message,'error'); else showToast('Entry created'); }}
              onStatusUpdate={handleStatusUpdate}
              onAddComment={async (entryId, comment) => { await supabase.from('time_entry_comments').insert({ time_entry_id:entryId, user_id:user.id, comment_text:comment }); showToast('Comment added'); }}
              onUpdateTags={async (entryId, tags) => { await updateTimeEntry(entryId, { tags }); }}
              userRole={userRole} teamMembers={teamMembers}
              goals={{ dailyHours:8, weeklyHours:40 }}
            />
          </div>
        )}

        {/* List view */}
        {viewMode==='list' && (
          <>
            {filteredEntries.length===0 ? (
              <div className="py-20 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><Inbox className="w-8 h-8 text-slate-300" /></div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No entries found</h3>
                <p className="text-slate-400 text-sm mb-4">{activeFilterCount>0?'Try adjusting or clearing your filters.':'No time entries exist yet.'}</p>
                {activeFilterCount>0 && <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-700 font-semibold">Clear all filters</button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="pl-4 pr-2 py-3"><Checkbox checked={allSelected} indeterminate={anySelected&&!allSelected} onChange={toggleAll} /></th>
                      {[{field:'task_name',label:'Task'},{field:'user_id',label:'User'},{field:'client_id',label:'Client / Project'},{field:'entry_date',label:'Date'},{field:'duration_hours',label:'Duration'},{field:'status',label:'Status'}].map(col => (
                        <th key={col.field} className="px-3 py-3">
                          <button onClick={()=>toggleSort(col.field)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide hover:text-slate-700">
                            {col.label}<SortIcon field={col.field} />
                          </button>
                        </th>
                      ))}
                      <th className="px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedEntries.map(group => (
                      <React.Fragment key={group.key}>
                        {groupBy!=='none' && (
                          <GroupHeader
                            label={group.label} count={group.entries.length}
                            hours={group.entries.reduce((s,e)=>s+(e.duration_hours||0),0)}
                            revenue={group.entries.filter(e=>e.is_billable).reduce((s,e)=>s+(e.duration_hours*(e.hourly_rate||0)),0)}
                            expanded={expandedGroups[group.key]!==false}
                            onToggle={()=>setExpandedGroups(p=>({...p,[group.key]:p[group.key]===false}))}
                            onSelectAll={()=>toggleGroup(group.entries)}
                            allSelected={group.entries.every(e=>selected.includes(e.id))}
                            anySelected={group.entries.some(e=>selected.includes(e.id))}
                          />
                        )}
                        {(groupBy==='none'||expandedGroups[group.key]!==false) && group.entries.map(entry => (
                          <EntryRow key={entry.id} entry={entry} selected={selected.includes(entry.id)}
                            onToggle={toggleOne} onEdit={setEditingEntry} onDelete={handleDelete} onDuplicate={handleDuplicate}
                            onStatusUpdate={(e,quickApprove) => quickApprove ? handleStatusUpdate(e,'approved') : setWorkflowEntry(e)}
                            onView={setDetailEntry} userRole={userRole} teamMembers={teamMembers} clients={clients} projects={projects} />
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredEntries.length>0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl text-xs text-slate-500">
                <span className="font-medium">{filteredEntries.length}{isFiltered?` of ${rawEntries.length}`:''} entries · {fmtHours(filteredStats.totalHours)}</span>
                <span className="font-semibold text-emerald-700">{fmtCurrency(filteredStats.revenue)} billable</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {editingEntry && <EditModal entry={editingEntry} teamMembers={teamMembers} clients={clients} projects={projects} userRole={userRole} onSave={handleEdit} onClose={()=>setEditingEntry(null)} />}
      {workflowEntry && <WorkflowModal entry={workflowEntry} teamMembers={teamMembers} userRole={userRole} onAction={handleStatusUpdate} onClose={()=>setWorkflowEntry(null)} />}
      {detailEntry && <DetailPanel entry={detailEntry} teamMembers={teamMembers} clients={clients} projects={projects} userRole={userRole} currentUserId={user?.id} onClose={()=>setDetailEntry(null)} onEdit={(e)=>{setDetailEntry(null);setEditingEntry(e);}} onStatusUpdate={(e)=>{setDetailEntry(null);setWorkflowEntry(e);}} onCommentAdded={refetch} />}
      {showCreateOnBehalf && <CreateOnBehalfModal teamMembers={teamMembers} clients={clients} projects={projects} onSave={handleCreateOnBehalf} onClose={()=>setShowCreateOnBehalf(false)} />}
    </div>
  );
};

export default ReviewEntriesBeta;
